import type { AppServices } from '@dcc/rules/app/services.ts';
import type { SheetMutationGate } from '@dcc/rules/app/sheetMutationGate.ts';
import type { SheetsApiStorage } from '../obr/sheetsApiStorage.ts';
import { activeEffects } from '@dcc/rules/rules/encounter.ts';
import { contentRegistry } from '@dcc/rules/content/dccCoreContent.ts';
import { createSession } from '@dcc/rules/domain/session.ts';
import { claimPendingUntrainedAward, discardPendingUntrainedAward } from '@dcc/rules/app/advancementService.ts';
import { applyUntrainedAward, isTutorialFloor } from '@dcc/rules/rules/sessionAdvancement.ts';
import { applyChanges } from '@dcc/rules/rules/characterChanges.ts';
import { batch } from '@dcc/rules/app/characterModificationService.ts';
import {
  healCharacterSlots,
  restoreCharacterMana,
  spendCharacterMana,
  takePostMitigationDamage,
} from '@dcc/rules/app/healthService.ts';
import { computeSheet } from '../services/characterService.ts';
import type { SheetActionHandlers } from '../components/SheetActions.tsx';
import { CharacterSheet } from '../components/CharacterSheet.tsx';
import { EditSheetPanel, type CreateCustomSkillResult } from '../components/EditSheetPanel.tsx';
import { applySheetEdit } from '../services/sheetEditService.ts';
import { createCustomDefinitionForEditDraft } from '@dcc/rules/app/customSkillEditCharacterFlow.ts';
import type { EditDraft } from '@dcc/rules/app/characterEditService.ts';
import type { CustomSkillDraft } from '@dcc/rules/rules/customSkills.ts';
import { useState } from 'react';
import { usePlaySheet } from './usePlaySheet.ts';
import { useCheckRoller } from './useCheckRoller.ts';
import type { RollMode } from '@dcc/rules/rules/rollModes.ts';
import { RollBreakdown } from './RollBreakdown.tsx';
import { isDamageEntry } from '@dcc/rules/rules/rollHistory.ts';

import { UnskilledPanel } from './UnskilledPanel.tsx';
import { CrawlerLevelUpPanel } from './CrawlerLevelUpPanel.tsx';
import { allocateCrawlerStatPoints } from '@dcc/rules/app/crawlerAdvancementService.ts';

const lookupItem = (id: string) => contentRegistry.getItem(id);

/**
 * The ordinary Sheet tab on the same Character + Session + Runtime ownership
 * path as Roll and Attack. The old character-only service is intentionally not
 * used here: a manual resource change now participates in the mutation gate,
 * version handling and checkpoint recovery used by every other play action.
 */
export function PlaySheetTab(props: {
  services: AppServices;
  characterId: string;
  refreshToken?: number;
  onSaveError: (error: unknown) => Promise<string>;
  coordinationGate: SheetMutationGate;
  combatStorage: SheetsApiStorage;
}) {
  const play = usePlaySheet(props.services, props.characterId, props.onSaveError, props.coordinationGate, props.refreshToken);
  const [editingSheet, setEditingSheet] = useState(false);
  const [rollMode, setRollMode] = useState<RollMode>('normal');
  const checkRoller = useCheckRoller(play);

  if (play.error && !play.records) return <p className="dcc-save-status dcc-save-warn" role="alert">{play.error}</p>;
  if (!play.records) return <p>Loading…</p>;

  const effects = activeEffects(play.records.runtime);
  const view = computeSheet(play.records.character, effects);
  const pendingAward = play.records.session.pendingUntrainedAward;
  const endCandidates = isTutorialFloor(play.records.character.floor)
    ? play.records.session.untrainedSkillsAttempted
    : [];
  const skillName = (id: string) => contentRegistry.getSkill(id)?.name ?? id;

  async function mutate(
    context: string,
    compute: Parameters<typeof play.run>[1],
    message: () => string | void,
  ): Promise<string | void> {
    const saved = await play.run(context, compute);
    return saved ? message() : undefined;
  }

  const handlers: SheetActionHandlers = {
    onDamage: async (amount) => {
      let text = '';
      return mutate('The damage could not be saved.', async (records) => {
        const out = takePostMitigationDamage(records.character, amount, activeEffects(records.runtime), lookupItem);
        text = out.healthBar.slotsLost === 0 && amount > 0
          ? `No slot lost — ${out.healthBar.damageIgnored} damage was under one slot (ignored).`
          : `${out.healthBar.slotsLost} slot(s) lost${out.healthBar.reducedToZero ? ' — down!' : ''}.`;
        return { character: out.character };
      }, () => text);
    },
    onHeal: async (slots) => {
      let text = '';
      return mutate('The healing could not be saved.', async (records) => {
        const before = records.character.healthBarSlotsRemaining;
        const character = healCharacterSlots(records.character, slots, activeEffects(records.runtime), lookupItem);
        text = `Healed ${character.healthBarSlotsRemaining - before} slot(s).`;
        return { character };
      }, () => text);
    },
    onSpendMana: async (cost) => {
      let text = '';
      return mutate('The Mana change could not be saved.', async (records) => {
        const out = spendCharacterMana(records.character, cost, activeEffects(records.runtime), lookupItem);
        if (!out.ok) { text = out.reason; return out.reason; }
        text = `Spent ${cost} mana.`;
        return { character: out.character };
      }, () => text);
    },
    onRestoreMana: async (amount) => {
      let text = '';
      return mutate('The Mana change could not be saved.', async (records) => {
        const character = restoreCharacterMana(records.character, amount, activeEffects(records.runtime), lookupItem);
        text = 'Restored mana.';
        return { character };
      }, () => text);
    },
    onRename: async (name) => {
      await play.run('The name change could not be saved.', async (records) => ({ character: { ...records.character, name } }));
    },
  };

  async function saveEditSheet(draft: EditDraft): Promise<boolean> {
    return play.run('The sheet edit could not be saved.', async (records) => {
      const changedStats = (['str', 'int', 'con', 'dex', 'cha'] as const).some(id => draft.stats[id] !== records.character.unenhancedStats[id]);
      const combat = changedStats ? await props.combatStorage.getCombat() : null;
      const result = applySheetEdit(records.character, draft, activeEffects(records.runtime), combat !== null || records.runtime.encounter !== null);
      if (!result.ok) return result.problems[0] ?? 'The sheet edit was rejected by the rules.';
      return { character: result.character, note: 'Sheet changes saved.' };
    });
  }

  async function createCustomSkillForEdit(
    draft: EditDraft,
    custom: CustomSkillDraft,
    startingRank: string,
  ): Promise<CreateCustomSkillResult> {
    let nextDraft: EditDraft | undefined;
    let responseMessage = '';
    const ok = await play.run('The custom Skill could not be created.', async (records) => {
      const outcome = await createCustomDefinitionForEditDraft(
        records.character,
        draft,
        custom,
        startingRank,
        {
          customSkills: props.services.customSkills,
          customBackgrounds: props.services.customBackgrounds,
          customItems: props.services.customItems,
          characters: props.services.repository,
          now: () => new Date().toISOString(),
        },
      );
      if (!outcome.ok) {
        responseMessage = outcome.problems[0]?.message ?? 'The custom Skill was rejected by the rules.';
        return responseMessage;
      }
      nextDraft = outcome.draft;
      responseMessage = `${outcome.skill.name} created. Save Sheet Changes to make Rank ${outcome.startingRank} permanent on this crawler.`;
      return { note: responseMessage };
    });

    if (!ok || !nextDraft) {
      return { ok: false, message: responseMessage || 'The custom Skill was not created.' };
    }
    return { ok: true, draft: nextDraft, message: responseMessage };
  }

  function claimPending(skillId: string) {
    void play.run('The advancement award could not be saved.', async (records) => {
      const outcome = claimPendingUntrainedAward(records.character, records.session, skillId);
      if (!outcome.ok) return outcome.reason;
      return {
        character: outcome.character,
        session: outcome.session,
        note: `${skillName(skillId)} gained Rank 1.`,
      };
    });
  }

  function discardPending() {
    if (!window.confirm('Discard this untrained Skill Rank award? This cannot be recovered from the sheet.')) return;
    void play.run('The advancement award could not be discarded.', async (records) => ({
      session: discardPendingUntrainedAward(records.session),
      note: 'The untrained Skill award was discarded.',
    }));
  }

  function endSessionWithAward(skillId: string) {
    void play.run('The session could not be ended.', async (records) => {
      if (records.session.pendingUntrainedAward !== null) {
        return 'Resolve the previous 2-hour checkpoint award before ending the session.';
      }
      const awarded = applyUntrainedAward({
        skills: records.character.skills,
        floor: records.character.floor,
        session: records.session,
        chosenSkillId: skillId,
      });
      if (!awarded.ok) return awarded.reason;
      const changed = applyChanges(
        records.character,
        batch('session-award', 'End-of-session untrained Skill award', [
          { kind: 'add-skill', skillId, rank: 1 },
        ]),
        { now: new Date().toISOString() },
      );
      if (!changed.ok) return changed.problems.map((problem) => problem.message).join('; ');
      return {
        character: changed.character,
        session: createSession(records.character.id, new Date().toISOString()),
        note: `${skillName(skillId)} gained Rank 1. Session ended.`,
      };
    });
  }

  function endSession(discard = false) {
    if (pendingAward) return;
    if (endCandidates.length > 0 && !discard) return;
    if (discard && !window.confirm('End the session and discard all untrained Skill candidates from this unfinished interval?')) return;
    if (!discard && !window.confirm('End this session and clear its roll history?')) return;
    void play.run('The session could not be ended.', async (records) => ({
      session: createSession(records.character.id, new Date().toISOString()),
      note: 'Session ended. A fresh session has started.',
    }));
  }

  return (
    <div className="dcc-play">
      <EditSheetPanel
        character={play.records.character}
        busy={play.busy}
        onEditingChange={setEditingSheet}
        onSave={saveEditSheet}
        onCreateCustom={createCustomSkillForEdit}
      />
      {!editingSheet ? (
      <CharacterSheet
          view={view}
          actions={handlers}
          unskilled={<UnskilledPanel character={view.character} effective={view.effective} session={play.records.session}
        disabled={checkRoller.disabled} onRoll={(skill, stat) => void checkRoller.roll({ skill, stat }, 'disadvantage', true)} />}
          rolls={{
            mode: rollMode,
            disabled: checkRoller.disabled,
            rolling: checkRoller.rolling,
            onModeChange: setRollMode,
            onRollStat: (stat, mode) => { void checkRoller.roll({ stat }, mode ?? rollMode); },
            onRollSkill: (skillId, stat, mode) => {
              const skill = contentRegistry.getSkill(skillId);
              if (skill) void checkRoller.roll({ stat, skill }, mode ?? rollMode);
            },
          }}
        />
      ) : null}
      {!editingSheet ? <section className="dcc-creator-card">
        <h2>Advancement</h2>
        <p className="dcc-creator-help">
          Level {play.records.character.level}. The GM's “2-Hour Play Checkpoint” rolls marked Rank 1–4 Skills and grants the Level automatically; it does not advance in-game time.
        </p>

        <CrawlerLevelUpPanel character={play.records.character} busy={play.busy} onConfirm={async (allocation, saferoom) => {
          return play.run('Stat Points could not be saved.', async (records) => {
            const combat = await props.combatStorage.getCombat();
            const out = allocateCrawlerStatPoints(records.character, allocation, saferoom, combat !== null || records.runtime.encounter !== null, new Date().toISOString());
            return out.ok ? { character: out.character, note: 'Stat Points allocated.' } : out.reason;
          });
        }} />
        <p className="dcc-creator-help">Marked Rank 5+ Skills advance at the GM’s End-of-Floor Skill Checkpoint. Gaining a Level does not raise Skill Ranks.</p>
        {pendingAward && (
          <>
            <p><strong>Untrained Skill award waiting.</strong> Choose one Skill attempted untrained during the completed Tutorial-floor interval:</p>
            <div className="dcc-creator-options">
              {pendingAward.candidates.map((id) => (
                <button key={id} disabled={play.busy} onClick={() => claimPending(id)}>
                  {skillName(id)} → Rank 1
                </button>
              ))}
            </div>
            <div className="dcc-creator-nav">
              <button className="dcc-creator-secondary" disabled={play.busy} onClick={discardPending}>Discard award</button>
            </div>
          </>
        )}

        {!pendingAward && endCandidates.length > 0 && (
          <>
            <p><strong>End-of-session untrained candidates.</strong> If the session ends before the next 2-hour checkpoint, choose one to gain Rank 1:</p>
            <div className="dcc-creator-options">
              {endCandidates.map((id) => (
                <button key={id} disabled={play.busy} onClick={() => endSessionWithAward(id)}>
                  {skillName(id)} → Rank 1 &amp; end session
                </button>
              ))}
            </div>
            <div className="dcc-creator-nav">
              <button className="dcc-creator-secondary" disabled={play.busy} onClick={() => endSession(true)}>End session &amp; discard candidates</button>
            </div>
          </>
        )}

        {!pendingAward && endCandidates.length === 0 && (
          <div className="dcc-creator-nav">
            <button className="dcc-creator-secondary" disabled={play.busy} onClick={() => endSession(false)}>End session</button>
          </div>
        )}
        {play.note && <p className="dcc-creator-help">{play.note}</p>}
      </section> : null}
      {!editingSheet && checkRoller.latest ? (
        <section className="dcc-creator-card">
          <h2>Latest Roll</h2>
          <RollBreakdown
            entry={checkRoller.latest}
            reroll={isDamageEntry(checkRoller.latest) ? undefined : {
              problem: checkRoller.rerollProblem,
              aiFavor: play.records.character.aiFavor,
              disabled: checkRoller.disabled,
              onReroll: checkRoller.rerollLatest,
            }}
          />
          {play.note && <p className="dcc-creator-help">{play.note}</p>}
          {checkRoller.recent.length > 0 && (
            <ul className="dcc-play-recent">
              {checkRoller.recent.map((entry) => <li key={entry.id}><strong>{entry.total}</strong> {entry.label}</li>)}
            </ul>
          )}
        </section>
      ) : null}
      {play.error && <p className="dcc-save-status dcc-save-warn" role="alert">{play.error}</p>}
    </div>
  );
}
